// src/services/milestoneService.js
import api from '../config/axios';

// ============= BASE PATHS (WITHOUT /api prefix - already in baseURL) =============
const TEMPLATE_PATH = '/milestones/templates';
const MILESTONE_PATH = '/milestones';
const PROGRESS_PATH = '/progress';
const EVIDENCE_PATH = '/evidence';
const SEGMENT_PATH = '/segments';
const SITE_PATH = '/sites';
const USER_PATH = '/users';

// ============= TEMPLATE OPERATIONS =============

/**
 * Get all milestone templates
 */
export const getAllTemplates = async () => {
  const response = await api.get(TEMPLATE_PATH);
  return response.data;
};

/**
 * Get milestone template by ID
 */
export const getTemplateById = async (id) => {
  const response = await api.get(`${TEMPLATE_PATH}/${id}`);
  return response.data;
};

/**
 * Get milestone templates by project type
 * @param {string} projectType - FO, TOWER
 */
export const getTemplatesByProjectType = async (projectType) => {
  const response = await api.get(TEMPLATE_PATH, {
    params: { projectType }
  });
  return response.data;
};

/**
 * Get milestone template tree (hierarchical)
 * @param {string} projectType - FO, TOWER
 */
export const getTemplateTree = async (projectType) => {
  const response = await api.get(`${TEMPLATE_PATH}/tree/${projectType}`);
  return response.data;
};

/**
 * Get root milestone templates
 * @param {string} projectType - FO, TOWER
 */
export const getRootTemplates = async (projectType) => {
  const response = await api.get(`${TEMPLATE_PATH}/roots/${projectType}`);
  return response.data;
};

/**
 * Get leaf milestone templates (for BOQ)
 * @param {string} projectType - FO, TOWER
 */
export const getLeafTemplates = async (projectType) => {
  const response = await api.get(`${TEMPLATE_PATH}/leaves/${projectType}`);
  return response.data;
};

/**
 * Create new milestone template
 * @param {object} templateData - Template data
 */
export const createTemplate = async (templateData) => {
  const response = await api.post(TEMPLATE_PATH, templateData);
  return response.data;
};

/**
 * Update milestone template
 * @param {number} id - Template ID
 * @param {object} templateData - Updated template data
 */
export const updateTemplate = async (id, templateData) => {
  const response = await api.put(`${TEMPLATE_PATH}/${id}`, templateData);
  return response.data;
};

/**
 * Delete milestone template (soft delete)
 * @param {number} id - Template ID
 */
export const deleteTemplate = async (id) => {
  await api.delete(`${TEMPLATE_PATH}/${id}`);
};

// ============= EVIDENCE DEFINITION OPERATIONS =============

/**
 * Get evidence definitions for a milestone template
 * @param {number} templateId - Template ID
 */
export const getEvidenceDefinitions = async (templateId) => {
  const response = await api.get(`${TEMPLATE_PATH}/${templateId}/evidence-definitions`);
  return response.data;
};

/**
 * Add evidence definition to template
 * @param {number} templateId - Template ID
 * @param {object} definitionData - Evidence definition data
 */
export const addEvidenceDefinition = async (templateId, definitionData) => {
  const response = await api.post(
    `${TEMPLATE_PATH}/${templateId}/evidence-definitions`,
    definitionData
  );
  return response.data;
};

/**
 * Update evidence definition
 * @param {number} templateId - Template ID
 * @param {number} definitionId - Evidence definition ID
 * @param {object} definitionData - Updated evidence definition data
 */
export const updateEvidenceDefinition = async (templateId, definitionId, definitionData) => {
  const response = await api.put(
    `${TEMPLATE_PATH}/${templateId}/evidence-definitions/${definitionId}`,
    definitionData
  );
  return response.data;
};

/**
 * Delete evidence definition
 * @param {number} templateId - Template ID
 * @param {number} definitionId - Evidence definition ID
 */
export const deleteEvidenceDefinition = async (templateId, definitionId) => {
  await api.delete(
    `${TEMPLATE_PATH}/${templateId}/evidence-definitions/${definitionId}`
  );
};

// ============= PROJECT MILESTONE OPERATIONS =============

/**
 * Setup milestones for segment (copy from template)
 * FIXED: Accepts single request object
 * 
 * @param {object} request - Complete request object
 * @param {number} request.segmentId - Segment ID
 * @param {string} request.projectType - Project type (FO/TOWER)
 * @param {array} request.milestoneCustomizations - Customizations
 * 
 * Example:
 * await setupMilestonesForSegment({
 *   segmentId: 123,
 *   projectType: "FO",
 *   milestoneCustomizations: [...]
 * });
 */
export const setupMilestonesForSegment = async (request) => {
  // ✅ FIXED: Extract segmentId from request
  if (!request || !request.segmentId) {
    throw new Error('Invalid request: segmentId is required');
  }
  
  const segmentId = request.segmentId;
  
  console.log('📤 Setting up milestones for segment:', segmentId);
  console.log('📦 Request body:', request);
  
  const response = await api.post(
    `/segments/${segmentId}/milestones/setup`,  // ← Now uses correct segmentId
    request  // ← Send full request as body
  );
  
  return response.data;
};

/**
 * Setup milestones for tower site (copy from template)
 * @param {number} siteId - Site ID
 * @param {object} setupData - Setup configuration
 */
export const setupMilestonesForSite = async (siteId, setupData) => {
  const response = await api.post(
    `${SITE_PATH}/${siteId}/milestones/setup`,
    setupData
  );
  return response.data;
};

/**
 * Get milestones for segment (flat list)
 * @param {number} segmentId - Segment ID
 */
export const getMilestonesBySegment = async (segmentId) => {
  const response = await api.get(`${SEGMENT_PATH}/${segmentId}/milestones`);
  return response.data;
};

/**
 * Get milestone tree for segment (hierarchical)
 * @param {number} segmentId - Segment ID
 */
export const getMilestoneTreeBySegment = async (segmentId) => {
  const response = await api.get(`${SEGMENT_PATH}/${segmentId}/milestones/tree`);
  return response.data;
};

/**
 * Get milestones for site (flat list)
 * @param {number} siteId - Site ID
 */
export const getMilestonesBySite = async (siteId) => {
  const response = await api.get(`${SITE_PATH}/${siteId}/milestones`);
  return response.data;
};

/**
 * Get milestone tree for site (hierarchical)
 * @param {number} siteId - Site ID
 */
export const getMilestoneTreeBySite = async (siteId) => {
  const response = await api.get(`${SITE_PATH}/${siteId}/milestones/tree`);
  return response.data;
};

/**
 * Get single project milestone by ID
 * @param {number} id - Milestone ID
 */
export const getMilestoneById = async (id) => {
  const response = await api.get(`${MILESTONE_PATH}/${id}`);
  return response.data;
};

/**
 * Update milestone bobot (weight)
 * @param {number} id - Milestone ID
 * @param {object} bobotData - Bobot update data
 */
export const updateMilestoneBobot = async (id, bobotData) => {
  const response = await api.put(`${MILESTONE_PATH}/${id}/bobot`, bobotData);
  return response.data;
};

/**
 * Update milestone volume
 * @param {number} id - Milestone ID
 * @param {object} volumeData - Volume update data
 */
export const updateMilestoneVolume = async (id, volumeData) => {
  const response = await api.put(`${MILESTONE_PATH}/${id}/volume`, volumeData);
  return response.data;
};

/**
 * Update milestone dates
 * @param {number} id - Milestone ID
 * @param {object} datesData - Dates update data
 */
export const updateMilestoneDates = async (id, datesData) => {
  const response = await api.put(`${MILESTONE_PATH}/${id}/dates`, datesData);
  return response.data;
};

/**
 * Recalculate milestone progress
 * @param {number} id - Milestone ID
 */
export const recalculateMilestoneProgress = async (id) => {
  const response = await api.post(`${MILESTONE_PATH}/${id}/recalculate`);
  return response.data;
};

/**
 * Delete project milestone (soft delete)
 * @param {number} id - Milestone ID
 */
export const deleteMilestone = async (id) => {
  await api.delete(`${MILESTONE_PATH}/${id}`);
};

// ============= PROGRESS OPERATIONS =============

/**
 * Submit progress for milestone
 * @param {number} milestoneId - Milestone ID
 * @param {object} progressData - Progress data
 */
export const submitProgress = async (milestoneId, progressData) => {
  const response = await api.post(
    `${MILESTONE_PATH}/${milestoneId}/progress`,
    progressData
  );
  return response.data;
};

/**
 * Get progress submissions for milestone
 * @param {number} milestoneId - Milestone ID
 */
export const getProgressByMilestone = async (milestoneId) => {
  const response = await api.get(`${MILESTONE_PATH}/${milestoneId}/progress`);
  return response.data;
};

/**
 * Get progress by ID
 * @param {number} progressId - Progress ID
 */
export const getProgressById = async (progressId) => {
  const response = await api.get(`${PROGRESS_PATH}/${progressId}`);
  return response.data;
};

/**
 * Approve progress submission
 * @param {number} progressId - Progress ID
 * @param {object} approvalData - Approval data
 */
export const approveProgress = async (progressId, approvalData) => {
  const response = await api.put(
    `${PROGRESS_PATH}/${progressId}/approve`,
    approvalData
  );
  return response.data;
};

/**
 * Reject progress submission
 * @param {number} progressId - Progress ID
 * @param {object} rejectionData - Rejection data
 */
export const rejectProgress = async (progressId, rejectionData) => {
  const response = await api.put(
    `${PROGRESS_PATH}/${progressId}/reject`,
    rejectionData
  );
  return response.data;
};

/**
 * Get all pending approvals
 * @param {object} filters - Optional filters
 */
export const getPendingApprovals = async (filters = {}) => {
  const response = await api.get(`${PROGRESS_PATH}/pending`, {
    params: filters
  });
  return response.data;
};

/**
 * Get pending approvals for segment
 * @param {number} segmentId - Segment ID
 */
export const getPendingApprovalsBySegment = async (segmentId) => {
  const response = await api.get(
    `${SEGMENT_PATH}/${segmentId}/progress/pending`
  );
  return response.data;
};

/**
 * Get pending approvals for site
 * @param {number} siteId - Site ID
 */
export const getPendingApprovalsBySite = async (siteId) => {
  const response = await api.get(
    `${SITE_PATH}/${siteId}/progress/pending`
  );
  return response.data;
};

/**
 * Get progress submissions by user
 * @param {number} userId - User ID
 */
export const getProgressByUser = async (userId) => {
  const response = await api.get(`${USER_PATH}/${userId}/progress`);
  return response.data;
};

/**
 * Search progress submissions
 * @param {object} searchParams - Search parameters
 */
export const searchProgress = async (searchParams = {}) => {
  const response = await api.get(`${PROGRESS_PATH}/search`, {
    params: searchParams
  });
  return response.data;
};

/**
 * Delete progress submission (soft delete)
 * @param {number} progressId - Progress ID
 */
export const deleteProgress = async (progressId) => {
  await api.delete(`${PROGRESS_PATH}/${progressId}`);
};

// ============= EVIDENCE OPERATIONS =============

/**
 * Upload evidence file
 * @param {number} progressId - Progress ID
 * @param {FormData} formData - File upload data
 */
export const uploadEvidence = async (progressId, formData) => {
  const response = await api.post(
    `${PROGRESS_PATH}/${progressId}/evidence/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

/**
 * Upload multiple evidence files
 * @param {number} progressId - Progress ID
 * @param {FormData} formData - Multiple files upload data
 */
export const bulkUploadEvidence = async (progressId, formData) => {
  const response = await api.post(
    `${PROGRESS_PATH}/${progressId}/evidence/bulk-upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

/**
 * Get evidence by ID
 * @param {number} evidenceId - Evidence ID
 */
export const getEvidenceById = async (evidenceId) => {
  const response = await api.get(`${EVIDENCE_PATH}/${evidenceId}`);
  return response.data;
};

/**
 * Download evidence file
 * @param {number} evidenceId - Evidence ID
 */
export const downloadEvidence = async (evidenceId) => {
  const response = await api.get(`${EVIDENCE_PATH}/${evidenceId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Get evidence list for progress
 * @param {number} progressId - Progress ID
 */
export const getEvidenceByProgress = async (progressId) => {
  const response = await api.get(`${PROGRESS_PATH}/${progressId}/evidence`);
  return response.data;
};

/**
 * Approve evidence file
 * @param {number} evidenceId - Evidence ID
 * @param {object} approvalData - Approval data
 */
export const approveEvidence = async (evidenceId, approvalData) => {
  const response = await api.put(
    `${EVIDENCE_PATH}/${evidenceId}/approve`,
    approvalData
  );
  return response.data;
};

/**
 * Reject evidence file
 * @param {number} evidenceId - Evidence ID
 * @param {object} rejectionData - Rejection data
 */
export const rejectEvidence = async (evidenceId, rejectionData) => {
  const response = await api.put(
    `${EVIDENCE_PATH}/${evidenceId}/reject`,
    rejectionData
  );
  return response.data;
};

/**
 * Bulk approve evidence files
 * @param {array} evidenceIds - Array of evidence IDs
 */
export const bulkApproveEvidence = async (evidenceIds) => {
  const response = await api.post(`${EVIDENCE_PATH}/bulk-approve`, {
    evidenceIds
  });
  return response.data;
};

/**
 * Bulk reject evidence files
 * @param {array} evidenceIds - Array of evidence IDs
 * @param {string} reason - Rejection reason
 */
export const bulkRejectEvidence = async (evidenceIds, reason) => {
  const response = await api.post(`${EVIDENCE_PATH}/bulk-reject`, {
    evidenceIds,
    reason
  });
  return response.data;
};

/**
 * Delete evidence file (soft delete)
 * @param {number} evidenceId - Evidence ID
 */
export const deleteEvidence = async (evidenceId) => {
  await api.delete(`${EVIDENCE_PATH}/${evidenceId}`);
};

/**
 * Prepare bulk download
 * @param {object} downloadParams - Download parameters
 */
export const prepareBulkDownload = async (downloadParams) => {
  const response = await api.post(
    `${EVIDENCE_PATH}/bulk-download/prepare`,
    downloadParams
  );
  return response.data;
};

/**
 * Download evidence as ZIP
 * @param {object} downloadParams - Download parameters
 */
export const downloadEvidenceZip = async (downloadParams) => {
  const response = await api.post(
    `${EVIDENCE_PATH}/bulk-download/zip`,
    downloadParams,
    {
      responseType: 'blob'
    }
  );
  return response.data;
};

/**
 * Download all evidence for segment
 * @param {number} segmentId - Segment ID
 */
export const downloadSegmentEvidence = async (segmentId) => {
  const response = await api.get(
    `${SEGMENT_PATH}/${segmentId}/evidence/download`,
    {
      responseType: 'blob'
    }
  );
  return response.data;
};

/**
 * Download all evidence for site
 * @param {number} siteId - Site ID
 */
export const downloadSiteEvidence = async (siteId) => {
  const response = await api.get(
    `${SITE_PATH}/${siteId}/evidence/download`,
    {
      responseType: 'blob'
    }
  );
  return response.data;
};

/**
 * Get evidence statistics for segment
 * @param {number} segmentId - Segment ID
 */
export const getSegmentEvidenceStats = async (segmentId) => {
  const response = await api.get(`${SEGMENT_PATH}/${segmentId}/evidence/stats`);
  return response.data;
};

/**
 * Get evidence statistics for site
 * @param {number} siteId - Site ID
 */
export const getSiteEvidenceStats = async (siteId) => {
  const response = await api.get(`${SITE_PATH}/${siteId}/evidence/stats`);
  return response.data;
};

/**
 * Get LEAF milestones for a FO Segment (isGroup = false only).
 * Used by IssueForm milestone dropdown — supervisor picks from
 * actual work milestones only, not group/header milestones.
 *
 * Reuses getMilestonesBySegment() — no new API endpoint needed.
 *
 * @param {number} segmentId
 * @returns {Array} flat list of leaf ProjectMilestoneResponse
 */
export const getLeafMilestonesBySegment = async (segmentId) => {
  const data = await getMilestonesBySegment(segmentId);
  const list = Array.isArray(data) ? data : (data.content ?? []);
  return list.filter((m) => m.isGroup === false);
};
 
/**
 * Get LEAF milestones for a Tower Site (isGroup = false only).
 * Used by IssueForm milestone dropdown — supervisor picks from
 * actual work milestones only, not group/header milestones.
 *
 * Reuses getMilestonesBySite() — no new API endpoint needed.
 *
 * @param {number} siteId
 * @returns {Array} flat list of leaf ProjectMilestoneResponse
 */
export const getLeafMilestonesBySite = async (siteId) => {
  const data = await getMilestonesBySite(siteId);
  const list = Array.isArray(data) ? data : (data.content ?? []);
  return list.filter((m) => m.isGroup === false);
};

// ============= HEALTH CHECK =============

/**
 * Health check for milestone templates API
 */
export const healthCheckTemplates = async () => {
  const response = await api.get(`${TEMPLATE_PATH}/health`);
  return response.data;
};

/**
 * Health check for project milestones API
 */
export const healthCheckMilestones = async () => {
  const response = await api.get(`${MILESTONE_PATH}/health`);
  return response.data;
};

/**
 * Health check for progress API
 */
export const healthCheckProgress = async () => {
  const response = await api.get(`${PROGRESS_PATH}/health`);
  return response.data;
};

/**
 * Health check for evidence API
 */
export const healthCheckEvidence = async () => {
  const response = await api.get(`${EVIDENCE_PATH}/health`);
  return response.data;
};

// ============= EXPORT DEFAULT =============

export default {
  // Templates
  getAllTemplates,
  getTemplateById,
  getTemplatesByProjectType,
  getTemplateTree,
  getRootTemplates,
  getLeafTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  
  // Evidence Definitions
  getEvidenceDefinitions,
  addEvidenceDefinition,
  updateEvidenceDefinition,
  deleteEvidenceDefinition,
  
  // Project Milestones
  setupMilestonesForSegment,
  setupMilestonesForSite,
  getMilestonesBySegment,
  getMilestoneTreeBySegment,
  getMilestonesBySite,
  getMilestoneTreeBySite,
  getLeafMilestonesBySegment,
  getLeafMilestonesBySite,
  getMilestoneById,
  updateMilestoneBobot,
  updateMilestoneVolume,
  updateMilestoneDates,
  recalculateMilestoneProgress,
  deleteMilestone,
  
  // Progress
  submitProgress,
  getProgressByMilestone,
  getProgressById,
  approveProgress,
  rejectProgress,
  getPendingApprovals,
  getPendingApprovalsBySegment,
  getPendingApprovalsBySite,
  getProgressByUser,
  searchProgress,
  deleteProgress,
  
  // Evidence
  uploadEvidence,
  bulkUploadEvidence,
  getEvidenceById,
  downloadEvidence,
  getEvidenceByProgress,
  approveEvidence,
  rejectEvidence,
  bulkApproveEvidence,
  bulkRejectEvidence,
  deleteEvidence,
  prepareBulkDownload,
  downloadEvidenceZip,
  downloadSegmentEvidence,
  downloadSiteEvidence,
  getSegmentEvidenceStats,
  getSiteEvidenceStats,
  
  // Health
  healthCheckTemplates,
  healthCheckMilestones,
  healthCheckProgress,
  healthCheckEvidence
};